import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Mail, Video, Phone, MapPin, Check } from 'lucide-react';
import { schedulingApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { isEmbedRequested, withEmbedParam, useEmbedAutoHeight } from '@/lib/scheduling/embed';

type PublicEventType = {
    name: string;
    description?: string | null;
    duration?: number | null;
    slug?: string | null;
    theme?: 'light' | 'dark' | null;
};

// Shape of GET /scheduling/public/bookings/:id — a display-only whitelist.
// The event type rides along in the same response (null if it was deleted), so
// there is no second, separately-failing metadata request.
type PublicBooking = {
    id: string;
    start_time: string;
    end_time: string;
    invitee_name: string;
    invitee_email: string;
    timezone?: string | null;
    location_type?: string | null;
    location_details?: string | null;
    meeting_url?: string | null;
    status?: string | null;
    payment_status?: string | null;
    // Webinar registrations only; used to decide the embedded (widget) layout.
    session_id?: string | null;
    event_type: PublicEventType | null;
};

type LoadState = 'loading' | 'ready' | 'not_found' | 'error';

export default function BookingSuccess() {
    const { bookingId } = useParams<{ bookingId: string }>();
    const [booking, setBooking] = useState<PublicBooking | null>(null);
    const [loadState, setLoadState] = useState<LoadState>('loading');
    const eventType = booking?.event_type ?? null;

    const isDark = eventType?.theme === 'dark';
    useEffect(() => {
        if (!isDark) {
            document.documentElement.classList.remove('dark');
            return;
        }
        const wasDark = document.documentElement.classList.contains('dark');
        document.documentElement.classList.add('dark');
        return () => {
            if (!wasDark) document.documentElement.classList.remove('dark');
        };
    }, [isDark]);

    // Only a definite 404 from the booking lookup means "not found". Anything
    // else (network, 5xx) is a failed request for a booking that may well exist,
    // and gets a retry rather than a false "doesn't exist".
    const load = useCallback(async () => {
        if (!bookingId) { setLoadState('not_found'); return; }
        setLoadState('loading');
        try {
            const res = await schedulingApi.getPublicBookingConfirmation(bookingId);
            if (res?.success && res.data?.id) {
                setBooking(res.data as PublicBooking);
                setLoadState('ready');
            } else {
                setLoadState((res as any)?.statusCode === 404 ? 'not_found' : 'error');
            }
        } catch {
            setLoadState('error');
        }
    }, [bookingId]);

    useEffect(() => { load(); }, [load]);

    // Reached from an embedded webinar form (BookEvent carries `?embed=1`):
    // same rule as the form — no page chrome inside the widget's box, and the
    // box resizes to this confirmation. Webinar registrations only.
    const [embedRequested] = useState(() => isEmbedRequested(window.location.search));
    const embedded = embedRequested && !!booking?.session_id;
    const [embedRoot, setEmbedRoot] = useState<HTMLDivElement | null>(null);
    useEmbedAutoHeight(embedRoot, embedded);

    const isAwaitingPayment = booking?.payment_status === 'pending';
    const [pollTimedOut, setPollTimedOut] = useState(false);
    const [checkingNow, setCheckingNow] = useState(false);

    const checkPaymentOnce = useCallback(async (): Promise<boolean> => {
        if (!bookingId) return false;
        try {
            const br = await schedulingApi.getPublicBookingConfirmation(bookingId);
            if (br?.data?.payment_status && br.data.payment_status !== 'pending') {
                setBooking(br.data as PublicBooking);
                return true;
            }
        } catch { /* transient — caller keeps polling */ }
        return false;
    }, [bookingId]);

    useEffect(() => {
        if (!isAwaitingPayment || !bookingId) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        let attempts = 0;
        const FAST_ATTEMPTS = 20;          // ~1 min at FAST_MS
        const FAST_MS = 3000;
        const SLOW_MS = 15000;
        const GIVE_UP_MS = 10 * 60 * 1000; // stop hitting the API after 10 min
        const startedAt = Date.now();

        const tick = async () => {
            if (cancelled) return;
            attempts += 1;
            const resolved = await checkPaymentOnce();
            if (cancelled || resolved) return;          // resolved → stop polling
            if (attempts >= FAST_ATTEMPTS) setPollTimedOut(true);
            if (Date.now() - startedAt >= GIVE_UP_MS) return;
            timer = setTimeout(tick, attempts >= FAST_ATTEMPTS ? SLOW_MS : FAST_MS);
        };

        timer = setTimeout(tick, FAST_MS);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [isAwaitingPayment, bookingId, checkPaymentOnce]);

    const handleCheckNow = useCallback(async () => {
        setCheckingNow(true);
        try { await checkPaymentOnce(); } finally { setCheckingNow(false); }
    }, [checkPaymentOnce]);

    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    // Event-type metadata is decoration. If it is missing (event type deleted
    // after booking) the booking is still real, so fall back to what the booking
    // itself carries rather than hiding it.
    const title = eventType?.name || 'Your meeting';
    const description = eventType?.description || '';
    const durationMinutes = eventType?.duration
        ?? (booking ? Math.round((new Date(booking.end_time).getTime() - new Date(booking.start_time).getTime()) / 60000) : 0);

    const googleLink = () => {
        if (!booking) return '#';
        const p = new URLSearchParams({
            action: 'TEMPLATE',
            text: title,
            dates: `${fmt(new Date(booking.start_time))}/${fmt(new Date(booking.end_time))}`,
            details: description,
            location: booking.meeting_url || booking.location_details || 'Online',
        });
        return `https://calendar.google.com/calendar/render?${p}`;
    };

    const outlookLink = () => {
        if (!booking) return '#';
        const p = new URLSearchParams({
            path: '/calendar/action/compose',
            rru: 'addevent',
            subject: title,
            startdt: new Date(booking.start_time).toISOString(),
            enddt: new Date(booking.end_time).toISOString(),
            body: description,
            location: booking.meeting_url || booking.location_details || 'Online',
        });
        return `https://outlook.live.com/calendar/0/deeplink/compose?${p}`;
    };

    const downloadICS = () => {
        if (!booking) return;
        const ics = [
            'BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT',
            `DTSTART:${fmt(new Date(booking.start_time))}`,
            `DTEND:${fmt(new Date(booking.end_time))}`,
            `SUMMARY:${title}`,
            `DESCRIPTION:${description}`,
            `LOCATION:${booking.meeting_url || booking.location_details || 'Online'}`,
            `UID:${booking.id}@photonx`, 'END:VEVENT', 'END:VCALENDAR',
        ].join('\r\n');
        const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
        const a = document.createElement('a');
        a.href = url; a.download = `${eventType?.slug || 'booking'}-${booking.id}.ics`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
    };

    if (loadState === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
            </div>
        );
    }

    if (loadState === 'error') {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center" data-testid="booking-load-error">
                    <h2 className="text-2xl font-bold mb-2">Couldn't load your booking</h2>
                    <p className="text-muted-foreground">
                        Your booking may still be confirmed — check your email for the confirmation.
                    </p>
                    <Button variant="outline" className="mt-5" onClick={() => load()}>
                        Try again
                    </Button>
                </div>
            </div>
        );
    }

    if (loadState === 'not_found' || !booking) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center" data-testid="booking-not-found">
                    <h2 className="text-2xl font-bold mb-2">Booking Not Found</h2>
                    <p className="text-muted-foreground">This booking doesn't exist or has been removed.</p>
                </div>
            </div>
        );
    }

    if (isAwaitingPayment) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center max-w-md" data-testid="payment-confirming">
                    {!pollTimedOut ? (
                        <>
                            <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto mb-5" />
                            <h2 className="text-2xl font-bold mb-2">Confirming your payment…</h2>
                            <p className="text-muted-foreground">
                                This usually takes a few seconds. Please don't close this page.
                            </p>
                        </>
                    ) : (
                        <>
                            <h2 className="text-2xl font-bold mb-2">Payment is being processed</h2>
                            <p className="text-muted-foreground">
                                This is taking longer than usual. We're still checking — this page will
                                update on its own the moment your payment is confirmed, and the
                                confirmation email (with the joining details) follows straight after.
                                If you didn't complete the payment, you can simply register again —
                                this attempt won't hold a seat.
                            </p>
                            <Button
                                variant="outline"
                                className="mt-5"
                                onClick={handleCheckNow}
                                disabled={checkingNow}
                                data-testid="payment-check-now"
                            >
                                {checkingNow ? 'Checking…' : 'Check again'}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    const start = new Date(booking.start_time);
    const end = new Date(booking.end_time);
    const timezone = booking.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    const LOCATION_LABELS: Record<string, string> = {
        google_meet: 'Google Meet',
        zoom: 'Zoom',
        teams: 'Microsoft Teams',
        phone: 'Phone call',
        in_person: 'In person',
        custom: 'Web conference',
    };
    const providerLabel = LOCATION_LABELS[booking.location_type || ''] || 'Web conference';
    const isVideoLocation = !['phone', 'in_person'].includes(booking.location_type || '');

    const timeStr = `${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })} – ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    const dateStr = start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

    return (
        <div
            ref={setEmbedRoot}
            className={embedded
                ? `px-6 py-8 sm:px-8 ${isDark ? 'dark bg-[#0b1120]' : 'bg-background'}`
                : `min-h-screen flex items-center justify-center py-10 px-4 ${isDark ? 'dark bg-[#0b1120]' : 'bg-muted/20'}`}
            data-testid={embedded ? 'booking-success-embedded' : undefined}
        >
            <div className={embedded ? 'w-full' : 'w-full max-w-[520px]'}>

                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-5">
                        <Check className="h-8 w-8 text-green-600 dark:text-green-400 stroke-[2.5]" />
                    </div>
                    <h1 className="text-2xl font-bold mb-1">You're Scheduled!</h1>
                    <p className="text-sm text-muted-foreground">
                        A confirmation email has been sent to{' '}
                        <span className="font-medium text-foreground">{booking.invitee_email}</span>
                    </p>
                </div>

                <div className="bg-background rounded-2xl border shadow-sm overflow-hidden mb-4">
                    <div className="px-6 py-5 border-b">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Booking Details</p>
                        <h2 className="text-lg font-bold">{title}</h2>
                    </div>

                    <div className="px-6 py-5 space-y-4">
                        <div className="flex items-start gap-3">
                            <Calendar className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            <div>
                                <p className="text-sm font-semibold">{dateStr}</p>
                                <p className="text-sm text-muted-foreground">{timeStr}</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                            <p className="text-sm text-muted-foreground">
                                {durationMinutes} minutes · {timezone}
                            </p>
                        </div>

                        <div className="flex items-start gap-3">
                            <Mail className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            <div>
                                <p className="text-sm font-semibold">{booking.invitee_name}</p>
                                <p className="text-sm text-muted-foreground">{booking.invitee_email}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            {booking.location_type === 'phone' ? (
                                <Phone className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            ) : booking.location_type === 'in_person' ? (
                                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            ) : (
                                <Video className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                            )}
                            <div className="space-y-2">
                                <p className="text-sm font-semibold">{providerLabel}</p>
                                {booking.meeting_url ? (
                                    <div className="flex flex-wrap items-center gap-3">
                                        <a
                                            href={booking.meeting_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input text-sm font-medium hover:bg-accent transition-colors"
                                        >
                                            Join {providerLabel}
                                        </a>
                                        <span className="text-xs text-muted-foreground break-all">{booking.meeting_url}</span>
                                    </div>
                                ) : booking.location_details ? (
                                    <p className="text-sm text-muted-foreground">{booking.location_details}</p>
                                ) : isVideoLocation ? (
                                    <p className="text-sm text-muted-foreground">
                                        The joining link will be shared with you by email before the meeting.
                                    </p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">Details in your confirmation email</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>


                <div className="text-center space-y-3">
                    {eventType?.slug && (
                        <Link
                            to={withEmbedParam(`/book/${eventType.slug}`, embedded)}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                        >
                            Schedule another meeting
                        </Link>
                    )}
                    {!embedded && (
                        <p className="text-xs text-muted-foreground">
                            © 2025 WeNext. Powered by PhotonX
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
